import type { Field } from '@blooq/flow'
import { IonButton, IonChip, IonIcon } from '@ionic/react'
import { checkmarkOutline } from 'ionicons/icons'
import { useEffect, useRef } from 'react'

import { tap } from '../../lib/native'
import { askOf, echoOf } from '../../lib/walk'

import { useChallenge } from './Challenge'
import { Composer } from './Composer'
import type { FieldWalk } from './FieldWalk'
import type { DockProps } from './StepDock'

/*
 * A form, asked as a conversation.
 *
 * One field at a time, in the order the flow lists them. A text field is the
 * composer; a choice is a row of chips; an optional one adds a Skip. Answering
 * the last field posts the whole set, so the server still receives exactly
 * what the step definition says it should — the walk is a rendering of the
 * form, not a change to it.
 *
 * Above it, the trail: one chip per field, ticked as it is answered, the way
 * a multi-question prompt shows its questions as tabs. It is the customer's
 * map of how much is left and their way back to something already said, and
 * with two or more fields it is the only hint that these questions belong to
 * one step.
 */
export function FormDock({ view, busy, walk, onSend }: DockProps) {
  const challenge = useChallenge(view)
  const { current, fields, index } = walk

  // A form with no fields has nothing to ask. The flow's validator forbids it,
  // so this is a guard rather than a state.
  if (!current) return null

  const last = index === fields.length - 1
  /* The bot check gates the SUBMISSION, so it appears with the last question
     and only that one holds until it is solved. */
  const gated = last && !challenge.ready

  const commit = (value: string) => {
    if (busy || gated) return
    tap()
    const payload = walk.answer(value)
    if (!payload) return

    const answer = echoOf(current, payload)
    onSend({ ...payload, ...challenge.payload }, [
      ...walk.exchanges,
      { prompt: askOf(current), answer, skipped: answer === undefined },
    ])
  }

  return (
    <div className="blooq-walk">
      {fields.length > 1 ? <Trail fields={fields} index={index} onJump={walk.jumpTo} /> : null}

      {walk.error ? <p className="blooq-err">{walk.error}</p> : null}
      {last ? challenge.node : null}

      {current.type === 'choice' ? (
        <ChoiceChips
          field={current}
          value={walk.draft[current.name] ?? ''}
          disabled={busy || gated}
          onPick={commit}
        />
      ) : (
        <Composer
          key={current.name}
          value={walk.draft[current.name] ?? ''}
          onChange={walk.set}
          onSubmit={() => commit((walk.draft[current.name] ?? '').trim())}
          placeholder={askOf(current)}
          kind={current.type}
          autocomplete={
            current.type === 'mobile'
              ? 'tel'
              : current.type === 'email'
                ? 'email'
                : /name/i.test(current.name)
                  ? 'name'
                  : 'off'
          }
          maxlength={current.maxLength}
          disabled={busy}
          ready={walk.ready && !gated}
          // Mid-walk the customer is typing; the first question waits for a tap.
          autofocus={index > 0}
        />
      )}

      {!current.required ? (
        <IonButton
          className="blooq-skip"
          fill="clear"
          size="small"
          disabled={busy || gated}
          onClick={() => commit('')}
        >
          {busy ? 'Please wait…' : 'Skip'}
        </IonButton>
      ) : null}
    </div>
  )
}

/**
 * The questions in this step, as a strip of chips.
 *
 * Answered ones are tappable and take the customer back to change that answer;
 * the current one is filled in the accent; the rest are ahead and inert, since
 * skipping forward would let a required question go unasked.
 */
function Trail({
  fields,
  index,
  onJump,
}: {
  fields: Field[]
  index: number
  onJump: (index: number) => void
}) {
  const rail = useRef<HTMLDivElement>(null)

  // Keep the current chip in view as the walk moves along a strip wider than
  // the screen — six questions do not fit across a phone.
  useEffect(() => {
    const chip = rail.current?.querySelector<HTMLElement>('[aria-current="step"]')
    chip?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
  }, [index])

  return (
    <div className="blooq-trail" ref={rail} role="group" aria-label="Questions in this step">
      {fields.map((field, i) => {
        const state = i < index ? 'done' : i === index ? 'now' : 'todo'
        return (
          <button
            key={field.name}
            type="button"
            className={`blooq-trail__chip blooq-trail__chip--${state}`}
            aria-current={state === 'now' ? 'step' : undefined}
            disabled={state === 'todo'}
            onClick={() => {
              if (state !== 'done') return
              tap()
              onJump(i)
            }}
          >
            <span className="blooq-trail__mark" aria-hidden="true">
              {state === 'done' ? <IonIcon icon={checkmarkOutline} /> : i + 1}
            </span>
            <span className="blooq-trail__text">{askOf(field)}</span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * A choice inside a form, as chips.
 *
 * Tapping one answers and moves on — a chip that only selects, waiting for a
 * Continue button, is a form control again. Returning to the question shows
 * the earlier pick filled, so changing it is one tap.
 */
function ChoiceChips({
  field,
  value,
  disabled,
  onPick,
}: {
  field: Field
  value: string
  disabled: boolean
  onPick: (value: string) => void
}) {
  return (
    <div className="blooq-chips" role="group" aria-label={askOf(field)}>
      {(field.options ?? []).map((option) => {
        const on = value === option.value
        return (
          <IonChip
            key={option.value}
            className={on ? 'blooq-chip blooq-chip--on' : 'blooq-chip'}
            disabled={disabled}
            role="button"
            aria-pressed={on}
            onClick={() => onPick(option.value)}
          >
            {option.label}
          </IonChip>
        )
      })}
    </div>
  )
}
